'use client';
import {cn} from '@lib/lib/cn';
import {Slot} from '@radix-ui/react-slot';
import {cva, type VariantProps} from 'class-variance-authority';
import {Loader2} from 'lucide-react';
import * as React from 'react';

const buttonVariants = cva(
  `inline-flex items-center justify-center whitespace-nowrap transition-all disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive focus-visible:ring-ring/50 focus-visible:ring-[2px] disabled:text-components-buttons-primary-text-disabled text-components-buttons-primary-text-default duration-200 ease-out`,
  {
    variants: {
      variant: {
        primary: `bg-components-buttons-primary-background-default active:bg-components-buttons-primary-background-pressed hover:bg-components-buttons-primary-background-hover focus:bg-components-buttons-primary-background-focus focus-visible:border-components-buttons-primary-stroke-focus disabled:bg-components-buttons-primary-background-disabled shadow-sm`,
        outline: `bg-components-buttons-outline-background-default border-components-buttons-outline-stroke-default text-components-buttons-outline-text-default hover:bg-components-buttons-outline-background-hover hover:text-components-buttons-outline-text-hover hover:border-components-buttons-outline-stroke-hover active:bg-components-buttons-outline-background-pressed active:text-components-buttons-outline-text-pressed focus:bg-components-buttons-outline-background-focus focus:border-components-buttons-outline-stroke-focus focus-visible:border-components-buttons-outline-stroke-focus shadow-[0_0_2px_0_rgba(24,39,75,0.18),_0_1px_3px_0_rgba(24,39,75,0.04)]`,
        subtle: `bg-components-buttons-subtle-background-default hover:bg-components-buttons-subtle-background-hover border-components-buttons-subtle-stroke-default hover:border-components-buttons-subtle-stroke-hover text-components-buttons-subtle-text-default active:bg-components-buttons-subtle-background-pressed focus-visible:ring-components-buttons-subtle-stroke-focus focus:ring-components-buttons-subtle-stroke-focus shadow-sm`,
        danger: `bg-components-buttons-danger-background-default hover:bg-components-buttons-danger-background-hover active:bg-components-buttons-danger-background-pressed focus:bg-components-buttons-danger-background-focus focus-visible:bg-components-buttons-danger-background-focus text-components-buttons-danger-text-default focus:ring-components-buttons-danger-stroke-focus focus-visible:ring-components-buttons-danger-stroke-focus shadow-sm`,
        link: `text-components-buttons-button-link-default-text-default hover:text-components-buttons-button-link-default-text-hover active:text-components-buttons-button-link-default-text-pressed focus:text-components-buttons-button-link-default-text-focused disabled:text-components-buttons-button-link-default-text-disabled underline-offset-4 hover:underline`,
        ghost: `bg-components-buttons-ghost-background-default hover:bg-background focus:bg-components-buttons-ghost-background-focus focus-visible:border-components-buttons-ghost-stroke-focus text-components-buttons-ghost-text-default hover:fill-components-buttons-ghost-text-hover [&>svg]:text-components-buttons-ghost-text-default hover:[&>svg]:text-components-buttons-ghost-text-hover`,
      },
      size: {
        default: `px-buttons-padding-md py-space-150 rounded-radius-rounded text-md font-(--font-weight-medium) h-buttons-height-md`,
        sm: `gap-1.5 has-[>svg]:px-2.5 p-space-150 text-md font-(--font-weight-medium) rounded-radius-rounded h-buttons-height-sm`,
        xs: `gap-1.5 has-[>svg]:px-2.5 p-buttons-padding-xs text-sm font-(--font-weight-medium) rounded-radius-rounded h-buttons-height-xs min-w-[24px]`,
        lg: `has-[>svg]:px-4 px-buttons-padding-lg py-space-200 rounded-radius-rounded font-semibold text-lg h-buttons-height-lg`,
        icon: `p-space-100 text-md font-(--font-weight-medium) rounded-radius-rounded h-buttons-height-sm`,
        iconXs: `p-space-050 text-sm font-(--font-weight-medium) rounded-radius-rounded h-buttons-height-xs`,
        iconLg: `p-space-200 rounded-radius-rounded font-semibold text-lg h-buttons-height-lg`,
      },
    },
    defaultVariants: {
      variant: `outline`,
      size: `sm`,
    },
  },
);

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean;
      loading?: boolean;
    }
>(({className, variant, size, asChild = false, loading = false, children, disabled, ...props}, ref) => {
  const sharedProps = {
    ref,
    'data-slot': `button`,
    className: cn(buttonVariants({variant, size, className})),
    disabled: disabled || loading,
    ...props,
  };

  if (asChild) {
    return <Slot {...sharedProps}>{children}</Slot>;
  }

  return (
    <button {...sharedProps}>
      {loading && <Loader2 className="absolute size-4 animate-spin" />}
      {loading ? <span className="opacity-0 inline-flex items-center gap-[inherit]">{children}</span> : children}
    </button>
  );
});

Button.displayName = 'Button';

export {Button, buttonVariants};
